import { InjectionToken } from '@angular/core';

/** An error Auth0 sent back on the login callback (Bug 37976). */
export interface Auth0CallbackError {
  /** The OAuth error code, for example `access_denied`. */
  error: string;
  /** Auth0's `error_description`, or the code when it sent none. */
  description: string;
}

/** The page query the app loaded with. A token so specs can supply it; the app reads `window.location.search`. */
export const AUTH0_CALLBACK_SEARCH = new InjectionToken<string>(
  'AUTH0_CALLBACK_SEARCH',
  {
    providedIn: 'root',
    factory: () => globalThis.location?.search ?? '',
  }
);

/** The longest description shown: it comes from the URL, so a crafted link can put any text in it (review of PR 8235). */
const MAX_DESCRIPTION = 200;

const capped = (text: string): string =>
  text.length <= MAX_DESCRIPTION
    ? text
    : `${text.slice(0, MAX_DESCRIPTION - 1)}\u2026`;

/**
 * Bug 37976: the error Auth0 returned on the login callback, or null. Auth0 always sends `state` or
 * `error_description` with an `error`, so a bare app query param named `error` is not read as a refused login.
 */
export function readAuth0CallbackError(
  search: string
): Auth0CallbackError | null {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  if (!error) return null;
  const description = params.get('error_description');
  if (!params.has('state') && description === null) return null;
  return { error, description: capped(description || error) };
}

/** The failure a held callback error gives a token request: auth0-spa-js's `error` / `error_description` shape. */
export function callbackErrorFailure(
  held: Auth0CallbackError
): Error & { error: string; error_description: string } {
  return Object.assign(new Error(held.description), {
    error: held.error,
    error_description: held.description,
  });
}
